import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  drawStrokes,
  extendStroke,
  PAD_HEIGHT,
  PAD_WIDTH,
  startStroke,
  toPadPoint,
  type Stroke,
} from '@/lib/signaturePad';
import { cn } from '@/lib/utils';

/**
 * The customer's drawing surface (M2-08, pass B): one hand-written canvas, no
 * library — Pointer Events give touch, pen and mouse one code path, as the
 * forms engine's `SignatureField` and the dispatch board already do.
 *
 * - `touch-action: none` (`touch-none`) so a finger drawing is not taken by
 *   the browser as a scroll before the stroke starts.
 * - Pointer capture, so a stroke that leaves the pad keeps its pointer and
 *   ends when the finger lifts, not when it crosses the edge.
 * - The drawing resolution is fixed ({@link PAD_WIDTH} × {@link PAD_HEIGHT})
 *   and CSS scales the canvas to its container; positions are scaled back
 *   (`toPadPoint`), so the PNG is the same size on every device.
 * - The strokes live with the caller; the canvas is redrawn from them.
 *
 * ⚠️ Not keyboard-operable, by nature. The keyboard path is the TYPED
 * signature, offered beside it — the hint under the pad says so.
 */
export function SignaturePad({
  strokes,
  onStrokesChange,
  canvasRef,
  labelId,
  describedBy,
  invalid,
  disabled,
}: {
  strokes: readonly Stroke[];
  onStrokesChange: (strokes: Stroke[]) => void;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  labelId: string;
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const drawing = useRef<number | null>(null);
  // Pointer events can arrive faster than React re-renders; each one extends
  // the LATEST strokes, not the ones this render was given.
  const latest = useRef<Stroke[]>([...strokes]);
  latest.current = [...strokes];

  useEffect(() => {
    // jsdom has no 2D context; a real browser always does.
    const context = canvasRef.current?.getContext('2d');
    if (context) drawStrokes(context, strokes);
  }, [canvasRef, strokes]);

  function point(event: PointerEvent<HTMLCanvasElement>) {
    return toPadPoint(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());
  }

  function onPointerDown(event: PointerEvent<HTMLCanvasElement>) {
    if (disabled || drawing.current !== null) return;
    // Only the primary button of a mouse; any touch or pen contact.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const at = point(event);
    if (!at) return;
    event.preventDefault();
    drawing.current = event.pointerId;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Not every environment implements capture; drawing works without it.
    }
    closeKeyboard(event.currentTarget.parentElement);
    latest.current = startStroke(latest.current, at);
    onStrokesChange(latest.current);
  }

  function onPointerMove(event: PointerEvent<HTMLCanvasElement>) {
    if (drawing.current !== event.pointerId) return;
    // A fast stroke on a phone reports several positions per frame; the
    // coalesced ones keep a quick signature from turning into straight lines.
    const native = event.nativeEvent as globalThis.PointerEvent;
    const events =
      typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const rect = event.currentTarget.getBoundingClientRect();
    let next = latest.current;
    for (const e of events.length > 0 ? events : [native]) {
      const at = toPadPoint(e.clientX, e.clientY, rect);
      if (at) next = extendStroke(next, at);
    }
    latest.current = next;
    onStrokesChange(next);
  }

  function onPointerEnd(event: PointerEvent<HTMLCanvasElement>) {
    if (drawing.current !== event.pointerId) return;
    drawing.current = null;
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={PAD_WIDTH}
        height={PAD_HEIGHT}
        role="img"
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        data-testid="signature-pad"
        style={{ aspectRatio: `${PAD_WIDTH} / ${PAD_HEIGHT}` }}
        className={cn(
          'block w-full touch-none rounded-md border bg-white select-none',
          invalid ? 'border-[var(--color-destructive)]' : 'border-[var(--color-input)]',
          disabled ? 'opacity-60' : 'cursor-crosshair',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onLostPointerCapture={onPointerEnd}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || strokes.length === 0}
        // No write to `latest` here: the re-render this causes resyncs it, and
        // no pointer event can land between the click and that render (a
        // mutation that dropped such a write survived, M2-08 pass B's C4).
        onClick={() => onStrokesChange([])}
      >
        {t('publicEstimate.answer.clearSignature')}
      </Button>
    </div>
  );
}

/** The input types a phone opens its keyboard for. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'password', 'number']);

/**
 * A stroke starting on the pad blurs the text field that has focus — the name,
 * typically — so a phone's keyboard closes instead of sitting over the pad
 * (Kyle, 2026-10-01). ⚠️ Needed because the pointerdown's `preventDefault`
 * (which keeps the stroke from turning into a scroll) also stops the mouse
 * events that would move focus off the field. Only a text input or textarea,
 * and only outside the pad: a focused radio, checkbox or button keeps focus.
 */
function closeKeyboard(pad: Element | null) {
  const active = document.activeElement;
  const typing =
    active instanceof HTMLTextAreaElement ||
    (active instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(active.type));
  if (typing && !pad?.contains(active)) active.blur();
}
